import { useCallback, useEffect, useState } from 'react';
import {
  getPendingTransfersToReceive,
  getBranchStockActivity,
  BRANCH_ACTIVITY_FETCH_LIMIT,
} from '../../../inventory/stock/public-api';
import { preflightCatalogSnapshotForRead } from '../../../catalog/infra-api';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import type { ScreenRefreshResult } from '../../../shared/ui/screenRefreshFeedback';
import { useInventoryUiStore } from '../../store/index';
import type { BranchStockActivityRow, GetStockMovementHistoryOptions } from '../../../inventory/stock/public-api';
import type { BranchActivityFeed } from '../../components/BranchActivityModal';

interface UseInventoryLoadersParams {
  currentBranchId: string | null;
  loadStocks: () => Promise<void>;
  refreshKpis: () => Promise<void>;
  stockHistoryRemoteOptions?: GetStockMovementHistoryOptions;
}

export const useInventoryLoaders = (params: UseInventoryLoadersParams) => {
  const { currentBranchId, loadStocks, refreshKpis, stockHistoryRemoteOptions } = params;

  const [branchActivityRows, setBranchActivityRows] = useState<BranchStockActivityRow[]>([]);
  const [branchActivityLoading, setBranchActivityLoading] = useState(false);
  // Error explícito: nunca renderizar "Sin actividad" cuando falló la carga.
  const [branchActivityError, setBranchActivityError] = useState<string | null>(null);

  const setPendingTransfers = useInventoryUiStore((s) => s.setPendingTransfers);
  const setPendingTransfersLoading = useInventoryUiStore((s) => s.setPendingTransfersLoading);

  const loadPendingTransfers = useCallback(async () => {
    if (!currentBranchId) return;
    setPendingTransfersLoading(true);
    try {
      const list = await getPendingTransfersToReceive(currentBranchId);
      setPendingTransfers(list);
    } catch (e) {
      // Un fallo acá no puede romper el refresh completo (Promise.all) ni
      // dejar la lista stale: vaciar y loguear.
      console.warn('[Inventory] Error cargando transferencias pendientes', e);
      setPendingTransfers([]);
    } finally {
      setPendingTransfersLoading(false);
    }
  }, [currentBranchId, setPendingTransfers, setPendingTransfersLoading]);

  const loadBranchActivity = useCallback(async () => {
    if (!currentBranchId) return;
    setBranchActivityLoading(true);
    setBranchActivityError(null);
    try {
      const list = await getBranchStockActivity(currentBranchId, BRANCH_ACTIVITY_FETCH_LIMIT, stockHistoryRemoteOptions);
      setBranchActivityRows(list);
    } catch (e) {
      if (!isDatabaseUnavailableError(e)) {
        console.warn('[Inventory] Error cargando actividad', e);
        setBranchActivityError('No pudimos cargar la actividad.');
      } else {
        setBranchActivityError(null);
      }
      setBranchActivityRows([]);
    } finally {
      setBranchActivityLoading(false);
    }
  }, [currentBranchId, stockHistoryRemoteOptions]);

  useEffect(() => {
    if (!currentBranchId) {
      setBranchActivityRows([]);
      setBranchActivityError(null);
      return;
    }
    void loadPendingTransfers();
  }, [currentBranchId, loadPendingTransfers]);

  const loadFn = useCallback(async (opts?: { force?: boolean }): Promise<ScreenRefreshResult | void> => {
    if (!currentBranchId) return { silent: true };

    if (opts?.force) {
      await preflightCatalogSnapshotForRead({ force: true }).catch(() => ({}));
      // allSettled: un loader que falla no aborta el refresh del resto.
      await Promise.allSettled([loadStocks(), loadPendingTransfers(), refreshKpis()]);
      return undefined;
    }

    await Promise.allSettled([loadStocks(), loadPendingTransfers(), refreshKpis()]);
  }, [currentBranchId, loadStocks, loadPendingTransfers, refreshKpis]);

  // Feed object para BranchActivityModal: el wiring intermedio
  // (useInventoryScreen / InventoryScreenModals) pasa el objeto tal cual.
  const branchActivity: BranchActivityFeed = {
    rows: branchActivityRows,
    error: branchActivityError,
    restricted: stockHistoryRemoteOptions?.useRemoteTeamHistory !== true,
    retry: () => {
      void loadBranchActivity();
    },
  };

  return {
    pendingTransfers: useInventoryUiStore((s) => s.pendingTransfers),
    pendingTransfersLoading: useInventoryUiStore((s) => s.pendingTransfersLoading),
    branchActivity,
    branchActivityLoading,
    loadPendingTransfers,
    loadBranchActivity,
    loadFn,
  };
};
